import React, { useContext } from 'react'
import { StepsContext } from '../contexts/Steps'
import { AdOrderContext } from '../contexts/AdOrder'
import { empty, isBool, isString } from '../utils/InputValidation'
import useGoToPayment from './useGoToPayment'

export default function useSteps () {
  const { steps, setSteps, currentStep, setCurrentStep, errors, setErrors } = React.useContext(StepsContext)
  const { adOrder, fieldSettings } = React.useContext(AdOrderContext)
  const { submit } = useGoToPayment()

  const isStep = function (step) {
    return currentStep === step
  }

  const getStep = function (step) {
    return steps[step]
  }

  const getCurrentStep = function () {
    return getStep(currentStep)
  }

  const goToStep = function(step, validate = true){
    window.scrollTo(0, 0)
    clearErrors()
    if (steps.length > step) {
      if(validate) {
        if (validateStep(currentStep) === true) {
          if(step === 3){
            submit(
              (errorMessage) => {
                setErrors([errorMessage])
              },
              () => {
                setCurrentStep(step)
              }
            )
          }else {
            setCurrentStep(step)
          }
        }
      }else {
        setCurrentStep(step)
      }
    } else {
      console.warn('No such step: ' + step)
    }
  }

  const nextStep = function () {
    goToStep(currentStep + 1)
  }
  const prevStep = function () {
    goToStep(currentStep - 1, false)
  }

  const validateStep = function (step) {
    switch (step) {
      case 0:
        return validateAdInformationStep()
      case 1:
        return validateContactInformationStep()
      case 2:
        return validatePaymentStep()
      default:
        return true
    }
  }

  const clearErrors = function () {
    setErrors(null)
  }

  const isRequired = (fieldPath) => {
    const keys = fieldPath.split('.')
    let setting = fieldSettings

    for (const key of keys) {
      setting = setting?.[key]
    }

    return setting?.required === true
  }

  const getTitle = (fieldPath) => {
    const keys = fieldPath.split('.')
    let setting = fieldSettings

    for (const key of keys) {
      setting = setting?.[key]
    }

    return setting?.title || 'Feltet'
  }

  const validateAdInformationStep = function(){
    let valid = true
    let errors = []

    if(isRequired('assignment_name') && (!isString(adOrder.name) || empty(adOrder.name))){
      valid = false
      errors.push(`${getTitle('assignment_name')} skal være angivet`)
    }

    if(isRequired('date_to_from') && empty(adOrder.date_from)){
      valid = false
      errors.push(`${getTitle('date_to_from')} - fra dato skal være angivet`)
    }

    if(isRequired('date_to_from') && empty(adOrder.date_to)){
      valid = false
      errors.push(`${getTitle('date_to_from')} - til dato skal være angivet`)
    }

    if(isRequired('cityportals') && empty(adOrder.cities)){
      valid = false
      errors.push(`${getTitle('cityportals')} skal være angivet`)
    }

    if(isRequired('deliver_add') && !isBool(adOrder.delivered_by_customer)){
      valid = false
      errors.push(`${getTitle('deliver_add')} skal være angivet`)
    }

    if(!adOrder.delivered_by_customer){
      if(isRequired('deliver_add.fields.headline') && (!isString(adOrder.headline) || empty(adOrder.headline))){
        valid = false
        errors.push(`${getTitle('deliver_add.fields.headline')} skal være angivet`)
      }

      if(isRequired('deliver_add.fields.date_time_and_place') && (!isString(adOrder.date_time_and_place) || empty(adOrder.date_time_and_place))){
        valid = false
        errors.push(`${getTitle('deliver_add.fields.date_time_and_place')} skal være angivet`)
      }

      if(isRequired('deliver_add.fields.website_link') && (!isString(adOrder.url) || (adOrder.url.indexOf('http://') !== 0 && adOrder.url.indexOf('https://') !== 0))){
        valid = false
        errors.push(`${getTitle('deliver_add.fields.website_link')} skal være angivet og starte med "http://" eller "https://"`)
      }

      if(isRequired('deliver_add.fields.text_for_add') && (!isString(adOrder.content) || empty(adOrder.content))){
        valid = false
        errors.push(`${getTitle('deliver_add.fields.text_for_add')} skal være angivet`)
      }
    }

    if(adOrder.delivered_by_customer && isRequired('image') && empty(adOrder.image)){
      valid = false
      errors.push(`${getTitle('image')} skal være valgt`)
    }

    setErrors(errors.length > 0 ? errors : null)
    return valid
  }

  const validateContactInformationStep = function(){
    let valid = true
    let errors = []

    let contact = adOrder.contact

    if(isRequired('name') && (!isString(contact.name) || empty(contact.name))){
      valid = false
      errors.push(`${getTitle('name')} skal være angivet`)
    }

    if(isRequired('company_name') && (!isString(contact.company_name) || empty(contact.company_name))){
      valid = false
      errors.push(`${getTitle('company_name')} skal være angivet`)
    }

    if(isRequired('cvr') && (!isString(contact.vat) || empty(contact.vat))){
      valid = false
      errors.push(`${getTitle('cvr')} skal være angivet`)
    }

    if((isRequired('mail') && (!isString(contact.email) || empty(contact.email))) || !contact.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      valid = false
      errors.push(`${getTitle('mail')} skal være angivet og gyldig`)
    }

    if(isRequired('phone') && (!isString(contact.phone) || empty(contact.phone))){
      valid = false
      errors.push(`${getTitle('phone')} skal være angivet og gyldig`)
    }

    setErrors(errors.length > 0 ? errors : null)
    return valid
  }

  const validatePaymentStep = function(){
    let valid = true
    let errors = []

    if(!isString(adOrder.payment?.method) || empty(adOrder.payment?.method)){
      valid = false
      errors.push(`${getTitle('choose_payment')} skal være valgt`)
    }

    if(adOrder.payment?.method === 'ean'){
      if(!isString(adOrder.payment?.ean) || empty(adOrder.payment?.ean)){
        valid = false
        errors.push(`${getTitle('choose_payment_invoice')} - EAN-nummer skal være angivet`)
      } else if(!/^\d{13}$/.test(adOrder.payment.ean)){
        valid = false
        errors.push(`${getTitle('choose_payment_invoice')} - EAN-nummer skal være præcis 13 cifre`)
      }
    }

    if(!isBool(adOrder.payment.accept) || empty(adOrder.payment.accept)){
      valid = false
      errors.push('Du skal acceptere vores handelsbetingelser')
    }

    setErrors(errors.length > 0 ? errors : null)
    return valid
  }

  return {
    currentStepIndex: currentStep,
    getCurrentStep,
    isStep,
    getStep,
    nextStep,
    prevStep,
    goToStep
  }
}